/** Crashes overlay layers, added to a SafeSystem app's own layers by `withCrashes`. */

import type { CircleLayerSpecification, HeatmapLayerSpecification } from 'maplibre-gl';
import type { IFeature, ILayerSource, TLayer } from '#lib/types.js';
import {
	ECrashesModule,
	ECrashes,
	EProduct,
	type CrashesLayerId,
	type ICrashesFeature,
	type ICrashesSystemLayers
} from './interfaces';
import { getVectorLayerSource } from '#lib/common/layers.js';

// --- Layer style JSON imports ---
import styleAccidentHeatmap from './layer_styles/accident_heatmap_style.json';
import styleAccidentCircle from './layer_styles/accident_circle_style.json';
import styleCustomCrashes from './layer_styles/custom_crashes_circle_style.json';

// GeoServer dataset carrying the crashes context layer.
export const CRASHES_DATASET = 'crashes';

function getGeoJSONLayerSource(
	layerId: CrashesLayerId,
	layerProps: Partial<TLayer>,
	zIndex: number
): ILayerSource {
	const sourceId = `${layerId}-source`;
	return {
		id: layerId,
		zIndex,
		title: layerId,

		source: {
			type: 'geojson',
			data: { type: 'FeatureCollection', features: [] },
			promoteId: 'id'
		} as ILayerSource['source'],
		layer: {
			...layerProps,
			id: `${layerId}`,
			source: sourceId
		}
	};
}

/**
 * Wraps a SafeSystem app's builder with the crashes overlay. The overlay is optional, so only the
 * app's own builder returning null (no data for the app) makes the result null.
 */
export function withCrashes<F extends IFeature, T>(build: (layers: F[]) => T | null) {
	return (layers: F[]): (T & ICrashesSystemLayers) | null => {
		const app = build(layers);
		return app && { ...app, ...crashesLayers(layers) };
	};
}

function crashesLayers(appLayers: IFeature[]): ICrashesSystemLayers {
	const crashes = appLayers.find((l) => l.dataset === CRASHES_DATASET);
	if (!crashes) return { [ECrashesModule.Crashes]: null };

	const feature: ICrashesFeature = {
		...crashes,
		product: EProduct.Context,
		module: ECrashesModule.Crashes,
		detail: crashes.keywords.detail
	};

	return {
		[ECrashesModule.Crashes]: {
			[ECrashes.CrashesHeatmap]: getVectorLayerSource(
				ECrashes.CrashesHeatmap,
				feature,
				{
					type: 'heatmap',
					layout: styleAccidentHeatmap.layout as HeatmapLayerSpecification['layout'],
					paint: styleAccidentHeatmap.paint as HeatmapLayerSpecification['paint'],
					minzoom: 6,
					maxzoom: 11
				},
				2
			),
			[ECrashes.CrashesCircle]: getVectorLayerSource(
				ECrashes.CrashesCircle,
				feature,
				{
					type: 'circle',
					layout: styleAccidentCircle.layout as CircleLayerSpecification['layout'],
					paint: styleAccidentCircle.paint as CircleLayerSpecification['paint'],
					minzoom: 9
				},
				1
			),
			[ECrashes.UploadedCrashes]: getGeoJSONLayerSource(
				ECrashes.UploadedCrashes,
				{
					type: 'circle',
					layout: styleCustomCrashes.layout as CircleLayerSpecification['layout'],
					paint: styleCustomCrashes.paint as CircleLayerSpecification['paint']
				},
				2
			)
		}
	};
}
